import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useLocation } from 'react-router';
import { toast } from 'sonner';
import { CreateTextCampaignAutomationRequest } from '../../../extViewmodels/sdk/models/requests/CreateAutomationRequest';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { invalidateAutomationTemplateFilter } from '../../../queries/AutomationTemplate/useAutomationTemplateFilterInfiniteQuery';
import { useTextingCampaignDraftSaveMutation } from '../../../queries/AutomationTemplate/useTextingCampaignDraftSaveMutation';
import { Button } from '../../components-v2/Button';
import {
	CampaignRecipientsSearch,
	CampaignRecipientsSearchResult,
} from '../../components/campaigns/CampaignRecipientsSearch';
import { FabContext } from '../../components/FabContext';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { SendFromDropdown } from '../../components/SendFromDropdown';
import { TextInput } from '../../components/TextInput';
import { bs } from '../../styles/styles';
import { CampaignComposerBaseLayout } from '../BulkHwcPages/presentation';
import { useTextingCampaignEditor } from './hooks/useTextingCampaignEditor';
import { styleSheet } from './styles';
import { TextCampaignPageBody } from './TextCampaignPageBody';

export function TextCampaignSearchPage() {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const location = useLocation<{
		initialSendOnBehalf?: Api.ISendOnBehalfPermission;
		initialOwnershipFilter?: Api.IOwnershipFilter;
		initialSendFromUserId?: string | null;
		initialCampaignTitle?: string;
		initialContentEditorState?: Api.IRichContentEditorState;
		initialAttachmentFiles?: File[];
		initialStoredAttachments?: Api.IFileAttachment[];
	}>();
	const [sendFrom, setSendFrom] = React.useState<Api.ISendOnBehalfPermission>(
		location.state?.initialSendOnBehalf ?? userSession.defaultSendOnBehalfPermissions[0]
	);
	const [ownershipFilter, setOwnershipFilter] = React.useState(location.state?.initialOwnershipFilter);
	const [sendFromUserId, setSendFromUserId] = React.useState<string>(location.state?.initialSendFromUserId);
	const { state, dispatch } = useTextingCampaignEditor({
		initialCampaignTitle: location.state?.initialCampaignTitle,
		initialContentEditorState: location.state?.initialContentEditorState,
		initialAttachmentFiles: location.state?.initialAttachmentFiles,
		initialStoredAttachments: location.state?.initialStoredAttachments,
	});

	// Save draft mutation
	const saveDraftMutation = useTextingCampaignDraftSaveMutation({
		onSuccess: () => {
			toast.success('Draft saved successfully');
			// Invalidate the drafts query so the new draft appears immediately
			invalidateAutomationTemplateFilter();
			fullscreenModal.history.push('/campaigns/texting');
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});
	const onSendOnBehalf = (option?: Api.ISendOnBehalfPermission, user?: Api.IUser) => {
		setSendFrom(option);
		setSendFromUserId(user?.id);
		setOwnershipFilter(option?.requiredFilter);
	};
	const handleNextClick = (value: CampaignRecipientsSearchResult) => {
		const initialContactIdsToInclude = value.selectedContacts.map(c => c.id);
		if (
			initialContactIdsToInclude.length > 0 &&
			!value.selectedContactSearch?.filter?.criteria &&
			value.selectedTags.length === 0
		) {
			fullscreenModal.history.push({
				pathname: `/texting-campaign/contacts`,
				state: {
					contactIds: initialContactIdsToInclude,
					initialSendOnBehalf: sendFrom,
					initialOwnershipFilter: ownershipFilter,
					initialSendFromUserId: sendFromUserId,
					initialCampaignTitle: state.campaignTitle,
					initialContentEditorState: state.contentEditorState,
					initialAttachmentFiles: state.attachmentFiles,
					initialStoredAttachments: state.storedAttachments,
				},
			});
			return;
		}

		const selectedCriteria = value.selectedContactSearch;
		let initialContactFilterCriteriaList: Api.IContactFilterCriteria[];
		if (selectedCriteria?.filter?.criteria) {
			initialContactFilterCriteriaList = selectedCriteria.filter.criteria;
		} else if (value.selectedTags.length > 0) {
			initialContactFilterCriteriaList = [
				{
					op: 'Or',
					criteria: value.selectedTags.map(tag => ({
						property: Api.ContactFilterCriteriaProperty.Tag,
						value: tag.name,
					})),
				},
			];
		}
		fullscreenModal.history.push({
			pathname: `/texting-campaign/filter`,
			state: {
				initialContactIdsToInclude,
				initialFilterCriteriaList: initialContactFilterCriteriaList,
				initialSendOnBehalf: sendFrom,
				initialSendFromUserId: sendFromUserId,
				initialOwnershipFilter: selectedCriteria?.ownershipFilter ?? ownershipFilter,
				initialCampaignTitle: state.campaignTitle,
				initialContentEditorState: state.contentEditorState,
				initialAttachmentFiles: state.attachmentFiles,
				initialStoredAttachments: state.storedAttachments,
			},
		});
	};

	const handleSaveDraft = () => {
		const trimmedTitle = state.campaignTitle.trim();
		if (!trimmedTitle) {
			errorMessages.push({
				messages: ['Please enter a campaign name'],
			});
			return;
		}

		const draftRequest: CreateTextCampaignAutomationRequest = {
			campaignName: trimmedTitle,
			content: state.contentEditorState.getRawRichTextContent(),
			autoStart: false,
			putOnHold: false,
			skipIfInProgress: true,
			skipIfInPrevious: false,
			sendFromOptions: sendFrom?.sender || { mode: Api.SendEmailFrom.CurrentUser },
			includeContactIds: [],
			excludeContactIds: [],
			attachments: state.storedAttachments,
			groupByHousehold: false,
		};

		saveDraftMutation.mutate({
			request: draftRequest,
			files: state.attachmentFiles,
		});
	};

	return (
		<>
			<MultiContainerHeader
				fullscreenHeader='New Texting Campaign'
				onFullscreenRequestBack={ev => {
					ev.preventDefault();
					fullscreenModal.dismissModal();
				}}
				onFullscreenRequestClose={() => fullscreenModal.dismissModal()}
			/>
			<CampaignComposerBaseLayout
				mainHeader={
					<div className={css(bs.px3, bs.py5, bs.flex, bs.gap2, bs.flexCol)}>
						<div className={css(bs.flex, bs.justifyBetween, bs.itemsCenter, bs.mb2)}>
							<h2 className={css(bs.textLg, bs.textTitles, bs.m0)}>New Texting Campaign</h2>
							<Button
								variant='secondary'
								size='sm'
								onClick={handleSaveDraft}
								disabled={!state.campaignTitle.trim() || saveDraftMutation.isPending}
								isLoading={saveDraftMutation.isPending}
							>
								Save as Draft
							</Button>
						</div>
						<div>
							<div className={css(bs.fontLexend, bs.mb1, bs.flex, bs.itemsCenter, bs.justifyBetween)}>
								<label
									htmlFor='textingCampaignName-input'
									className={css(bs.textSm, bs.textGray600, styleSheet.labelRequired)}
								>
									Campaign Title
								</label>
								<span className={css(bs.fontLight, bs.textDestructive, bs.textXs)}>Required Field</span>
							</div>
							<TextInput
								inputId='textingCampaignName-input'
								type='text'
								placeholder='Add title...'
								className={css(bs.roundedLg)}
								value={state.campaignTitle}
								onChange={ev => dispatch({ type: 'setCampaignTitle', value: ev.target.value })}
							/>
						</div>
						<SendFromDropdown
							disabled={!userSession.canSendOnBehalf}
							canSendOnBehalf
							sendOnBehalfOf={onSendOnBehalf}
							sendFromOption={sendFrom}
							allowedContactBasedOptions='all'
						/>
					</div>
				}
				sideBar={<CampaignRecipientsSearch onSubmit={handleNextClick} />}
			>
				<>
					<FabContext appearance={{ hidden: true }} />
					<div
						className={css(
							bs.itemsCenter,
							bs.boxBorder,
							bs.flex,
							bs.flex1,
							bs.flexCol,
							bs.overflowAuto,
							bs.py8,
							bs.px6,
							bs.bgGray50
						)}
					>
						<div className={css(bs.flex, bs.flexCol, bs.maxWXl, bs.wFull, bs.mxAuto)}>
							<TextCampaignPageBody
								contentEditorState={state.contentEditorState}
								attachmentFiles={state.attachmentFiles}
								storedAttachments={state.storedAttachments}
								onAttachFiles={files => dispatch({ type: 'addAttachmentFiles', value: files })}
								onRemoveAttachmentFile={file => dispatch({ type: 'removeAttachmentFile', value: file })}
								onRemoveStoredAttachment={attachmentId =>
									dispatch({ type: 'removeStoredAttachment', value: attachmentId })
								}
								onContentStateChanged={content => dispatch({ type: 'setTextingContentEditorState', value: content })}
							/>
						</div>
					</div>
				</>
			</CampaignComposerBaseLayout>
		</>
	);
}
